import { isNil } from 'lodash';
import { useContext, useEffect, useState } from 'react';
import { useLayoutConfig } from '@shared/components/blocks/contexts/layoutConfig';
import { CardRenderContext } from '@shared/components/blocks/contexts/render';
import { ViewTemplateContext } from '@shared/components/blocks/contexts/viewTemplate';
import { isComponentReadOnlyInView } from '@shared/data/cards/utils';
import { useWorkflowTemplate } from '@shared/scenes/workflows/hooks';
import { WorkflowComponent } from '@shared/scenes/workflows/views/view-components/WorkflowComponent';
import { CardFormContext } from './contexts/form';
import { CommonFields, Fields } from './types';

interface Props {
  fields: CommonFields & Fields;
  componentId: string;
  workflowTemplateId: string;
}

export function NoCodeBlockContainer({ fields, componentId, workflowTemplateId }: Props) {
  const [isReadOnly, setIsReadOnly] = useState(false);
  const { workflowTemplate } = useWorkflowTemplate(workflowTemplateId);
  const viewTemplate = useContext(ViewTemplateContext);
  const component = workflowTemplate?.components.find(c => c.id === componentId);
  const renderContext = useContext(CardRenderContext);
  const isCollectionView = ['list', 'table'].includes(renderContext);
  let iconPad = isCollectionView ? 1 : 2;
  if (renderContext === 'calendar') {
    iconPad = 0;
  }
  const {
    staticBlock: { displayLabel },
  } = useLayoutConfig();
  const { setFieldValue } = useContext(CardFormContext);

  const componentOptions = viewTemplate?.componentsViewOptions?.find(
    options => options.componentId === component?.id,
  );

  useEffect(() => {
    if (isNil(component) || isNil(viewTemplate)) {
      return;
    }

    setIsReadOnly(isComponentReadOnlyInView(component, viewTemplate));
  }, [component, viewTemplate]);

  if (isNil(component)) {
    return null;
  }

  return (
    <WorkflowComponent
      component={component}
      components={workflowTemplate?.components}
      fields={fields}
      onDataChanged={(id, value) => setFieldValue(id, value)}
      initialData={fields[component?.id]}
      isReadOnly={isReadOnly}
      showLabel={!viewTemplate?.defaultHiddenLabels && !!displayLabel}
      paddingHorizontal={isCollectionView ? 2 : 3}
      iconPad={iconPad}
      overrides={componentOptions?.overrides ?? undefined}
    />
  );
}
